"use client"

import type { GroupedSelectOptions, SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"
import type { FunctionComponent } from "react"

import { useEffect, useState } from "react"

import { Briefcase } from "@jobteaser/spark/components/Icons/Briefcase"

import { useJobAdsFilterNavigation } from "@/modules/jobAds/client/hooks/useJobAdsFilterNavigation"
import { useJobAdsFilterOptionsWithFacets } from "@/modules/jobAds/client/hooks/useJobAdsFilterOptionsWithFacets"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { getInitialOptions } from "@/modules/jobAds/client/utils/generateOptions"
import { MultiSelect } from "@/modules/layouts/components/MultiSelect/MultiSelect"

type ContractFilterProps = {
  applyLabel: string
  className?: string
  onApply?: (selectedOptions: SimpleSelectOptions[]) => void
  placeholder: string
}

export const ContractFilter: FunctionComponent<ContractFilterProps> = ({
  className,
  placeholder,
  applyLabel,
  onApply,
}) => {
  const { navigateWithUpdatedQueryParams } = useJobAdsFilterNavigation()
  const searchParams = useJobAdsSearchParams()
  const contractQuery = searchParams.getAll("contract") || undefined
  const contractOptions = useJobAdsFilterOptionsWithFacets("contract_type", "contract") as GroupedSelectOptions[]
  const contractOptionInitialValue = getInitialOptions({ options: contractOptions, param: contractQuery })
  const [contracts, setContracts] = useState<SimpleSelectOptions[]>(contractOptionInitialValue)

  const updateSearch = (selectedOptions: SimpleSelectOptions[]): void => {
    navigateWithUpdatedQueryParams({ availableFilters: { contract: selectedOptions.map(option => option.value) } })
    setContracts(selectedOptions)
    onApply?.(selectedOptions)
  }

  useEffect(() => {
    setContracts(getInitialOptions({ options: contractOptions, param: contractQuery }))
    // contractOptions is causing infinite loop
    // contractQuery is an array so we join its items to have a string that we can compare
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractQuery.join(",")])

  return (
    <MultiSelect
      className={className}
      id="job-ads-contract-filter"
      options={contractOptions.flatMap(({ options }) => options)}
      value={contracts}
      placeholder={placeholder}
      icon={Briefcase}
      onClose={selectedValues => updateSearch(selectedValues)}
      hasApplyButton
      applyLabel={applyLabel}
    />
  )
}
